<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import locationService from '@/services/location.service'
import LocationDrawer from '@/views/masters/location/drawer/LocationDrawer.vue'
import { refDebounced } from '@vueuse/core'
import { VDataTableServer } from 'vuetify/labs/VDataTable'

const searchQuery = ref('')
const isConfirmDialogVisible = ref(false)
const selectLocationType =ref(1)

definePage({
  meta: {
    name: 'master-location',
    subject: 'global.menu',
    action: 'master_location',
  },  
})

const snackbar = useToast()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data){
    
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order.toUpperCase()
}

// Headers
const headers = [
  {
    title: 'Name',
    key: 'name',
  },
  {
    title: 'Code',
    key: 'code',
  },
  {
    title: 'Parent Location',
    key: 'parent.name',
    sortable: false,
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Unit',
    key: 'unit.name',
    sortable: false,
  },
  {
    title: 'Actions',
    key: 'actions',
    sortable: false,
  },
]


const { data: locationTypes } = await useApi(createUrl('/masters/location-types', {
  query: {
    search: {
      unit_id: cookieRef('selectedUnit'),
    },
  },
}))

const debouncedSearch = refDebounced(searchQuery, 500)

const {
  data: locationsData,
  execute: fetchLocations,
} =  await useApi(createUrl('/masters/locations', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
    search: {
      location_type_id: selectLocationType,
      unit_id: cookieRef('selectedUnit'),
    },
  },
}))


const locations = computed(() => locationsData.value?.data)
const totalLocations = computed(() => locationsData.value?.meta?.total)

const updateLocation = data => {
  if (data.id) {
    locationService.updateLocation(
      data.id,
      data,
      () => {
        fetchLocations()
        isLocationDrawerVisible.value = false
        resetErrors()
      },
      onError,
    )
  }
}

const saveLocation = data => {
  locationService.addLocation(
    data,
    () => {
      fetchLocations()
      isLocationDrawerVisible.value = false
      resetErrors()
    },
    onError,
  )
}

const resetErrors = () => {
  errors.value = {
    name: [],
    code: [],
    status: [],
  }
}

const isLocationDrawerVisible = ref(false)
const locationDrawerData = ref()
const locationDeleteData = ref()
const locationDrawerType = ref('Add')

const showLocation = (locationData, type) => {
  locationDrawerData.value = locationData
  locationDrawerType.value = type  
  isLocationDrawerVisible.value = true
}

const deleteLocation = async (confirm, data) => {
  if(data?.id && confirm ){
    await locationService.deleteLocation(data.id, () => {
      fetchLocations()
      isConfirmDialogVisible.value = false
    }, onError)
  }
}

const showConfirmDialog = locationData => {
  locationDeleteData.value = locationData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value){
    locationDeleteData.value = {}
  }
})

// ===== Import Location Functionality =====
const refImportLocationForm = ref()
const importLocationFile = ref()
const importLocationErrors = ref()
const isImportLocationLoading = ref(false)
const isImportLocationModal = ref(false)

const submitImportLocation = async () => {
  if (!importLocationFile.value) {
    importLocationErrors.value = { file: ["Please select a file to import."] }

    return
  }
  isImportLocationLoading.value = true
  importLocationErrors.value = null // Clear previous errors

  try {
    await locationService.import(
      {
        file: importLocationFile.value,
      },
      () => {
        // onSuccess callback
        snackbar.success("Locations imported successfully.")
        isImportLocationModal.value = false
        fetchLocations() // Refresh the location list
        importLocationFile.value = null // Reset file input
      },
      error => {
        // onError callback
        importLocationErrors.value = error.data?.errors || error._data?.message || {
          message:
            error.data?.message || "File upload failed. Please try again.",
        }
      },
    )
  } catch (e) {
    console.error("Error during location import submission:", e)
    snackbar.error(
      "An unexpected error occurred while submitting the import request.",
    )
    if (!importLocationErrors.value)
      importLocationErrors.value = { message: "Import submission failed." }
  } finally {
    isImportLocationLoading.value = false
  }
}

const downloadSampleLocationFile = async () => {
  await downloadFile(
    "masters/locations/sample-import-file/export",
    "locations-sample-excel.xlsx",
    () => {}, // success
    () => {}, // error
  )
}

watch(
  () => isImportLocationModal.value,
  value => {
    if (value === false) {
      importLocationFile.value = null
      importLocationErrors.value = null
    }
  },
)

// ===== End Import Location Functionality =====
</script>

<template>
  <section>
    <VCard>
      <VCardText class="px-1">
        <VItemGroup
          v-model="selectLocationType"
          selected-class="bg-primary "
          mandatory
        >
          <VContainer>
            <VRow>
              <VCol
                v-for="locationType in locationTypes"
                :key="locationType.id"
                cols="12"
                md="2"
              >
                <VItem
                  v-slot="{isSelected, selectedClass, toggle }"
                  :value="locationType.id"
                >
                  <VCard
                    class="d-flex align-center cursor-pointer"
                    :class="[selectedClass]"
                    height="50"
                    variant="outlined"
                    dark
                    @click="toggle"
                  >
                    <div
                      class="text-boyd-2 flex-grow-1 text-center"
                      :class="isSelected ? 'text-white' : ''"
                    >
                      {{ locationType.name }}
                    </div>
                  </VCard>
                </VItem>
              </VCol>
            </VRow>
          </VContainer>
        </VItemGroup>
      </VCardText>
    </VCard>
    <VCard class="mt-4">
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <!-- 👉 Export button -->
          <!--
            <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-screen-share"
            >
            Export
            </VBtn> 
          -->

          <!-- 👉 Import Locations Button -->
          <VBtn
            v-if="$can('import', 'master.location')"
            prepend-icon="tabler-download"
            @click="isImportLocationModal = !isImportLocationModal"
          >
            Import Locations
          </VBtn>
          <VBtn             
            v-if="$can('create', 'master.location')"
            prepend-icon="tabler-plus"
            @click="showLocation({},'Add')"
          >
            Add New Location
          </VBtn>
        </div>
      </VCardText>
    </VCard>
    <VCard class="mt-4">
      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="locations ?? []"
        :items-length="totalLocations??0"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
            :locationData="item"
            @click="showLocation(item,'View')"
          >
            <VAvatar
              size="34"
              :variant="!item.avatar ? 'tonal' : undefined"
              class="me-3"
            >
              <VImg
                v-if="item.avatar"
                :src="item.avatar"
              />
              <span v-else>{{ avatarText(item.name) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>
        <template #item.code="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.code }}</span>
        </template>
        <template #item.status="{ item }">
          <span class="text-capitalize font-weight-medium">{{ item.status?'Active':"Inactive" }}</span>
        </template>
        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn             
            v-if="$can('delete', 'master.location')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn             
            v-if="$can('update', 'master.location')"
            @click="showLocation(item,'Edit')"
          >
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <!--
          <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
          <p class="text-sm text-disabled mb-0">
          {{ paginationMeta({ page, itemsPerPage }, totalLocations) }}
          </p>

          <VPagination
          v-model="page"
          :length="Math.ceil(totalLocations / itemsPerPage)"
          :total-visible="$vuetify.display.xs ? 1 : Math.ceil(totalLocations / itemsPerPage)"
          >
          <template #prev="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Previous
          </VBtn>
          </template>

          <template #next="slotProps">
          <VBtn
          variant="tonal"
          color="default"
          v-bind="slotProps"
          :icon="false"
          >
          Next
          </VBtn>
          </template>
          </VPagination>
          </div>
          </template> 
        -->
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>  
    <LocationDrawer
      v-model:isDrawerOpen="isLocationDrawerVisible"
      :location-data="locationDrawerData"
      :type="locationDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="locationDrawerType = $event"
      @update:location-data="updateLocation"
      @save-location="saveLocation"
    />
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Location Deleted."
      confirm-title="Deleted!"
      :data="locationDeleteData"
      @confirm="deleteLocation"
    />

    <!-- Import Location Modal -->
    <VDialog
      v-model="isImportLocationModal"
      max-width="600"
      :persistent="isImportLocationLoading"
    >
      <DialogCloseBtn
        size="small"
        :disabled="isImportLocationLoading"
        @click="isImportLocationModal = false"
      />
      <VCard>
        <VCardText class="pt-2">
          <h6 class="text-lg font-weight-medium mt-5">
            Import Locations
          </h6>
        </VCardText>

        <VCardText>
          <VForm ref="refImportLocationForm">
            <VFileInput
              label="Upload File"
              prepend-icon="tabler-file-import"
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              :disabled="isImportLocationLoading"
              :error-messages="importLocationErrors?.file"
              @change="
                (e) => {
                  importLocationFile = e.target.files[0];
                }
              "
            />
          </VForm>
        </VCardText>

        <!-- General Errors -->
        <VCardText
          v-if="
            importLocationErrors?.message &&
              !Array.isArray(importLocationErrors)
          "
        >
          <VAlert
            color="error"
            variant="tonal"
            closable
            @click:close="importLocationErrors.message = null"
          >
            {{ importLocationErrors.message }}
          </VAlert>
        </VCardText>

        <!-- Validation Errors (row-specific from backend) -->

        <VCardText v-if="Array.isArray(importLocationErrors) && importLocationErrors.length > 0">  
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="text-error text-caption font-weight-medium">
              {{ importLocationErrors.length }}
              {{ importLocationErrors.length === 1 ? 'Error' : 'Errors' }} found
            </div>
            <VBtn
              icon
              color="error"
              variant="text"
              size="small"
              class="ma-0 pa-0"
              density="compact"
              @click="importLocationErrors = []"
            >
              <VIcon
                color="error"
                size="18"
                icon="tabler-x"
              />
            </VBtn>
          </div>
          <div
            class="d-flex flex-column gap-2 scrollable-errors"
            style="max-height: 90px; overflow-y: auto;"
          >
            <VAlert
              v-for="(error, index) in importLocationErrors"
              :key="index"
              color="error"
              variant="tonal"
              density="compact"
              style="flex-shrink: 0;"
            >
              {{ error.row ? `Row ${error.row}: ` : "" }}{{ error.message || error }}
            </VAlert>
          </div>
        </VCardText>

        <!-- Loading Bar -->
        <VCardText
          v-if="isImportLocationLoading"
          class="pt-0 pb-2"
        >
          <VProgressLinear
            indeterminate
            color="primary"
            height="6"
            rounded
            class="mb-1"
          />
        </VCardText>

        <!-- Action Buttons -->
        <VCardActions class="px-5 pb-5">
          <VBtn
            variant="tonal"
            color="primary"
            :disabled="isImportLocationLoading"
            @click="downloadSampleLocationFile"
          >
            Download Sample
          </VBtn>
          <VSpacer />
          <VBtn
            variant="tonal"
            color="#EF5350"
            :disabled="isImportLocationLoading"
            @click="isImportLocationModal = false"
          >
            Close
          </VBtn>
          <VBtn
            variant="flat"
            :loading="isImportLocationLoading"
            :disabled="isImportLocationLoading || !importLocationFile"
            @click="submitImportLocation"
          >
            Import
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style lang="css">
/* For Chrome, Edge, Safari */
.scrollable-errors::-webkit-scrollbar {
  width: 2px;               /* thin scrollbar */
}

.scrollable-errors::-webkit-scrollbar-thumb {
  background-color: rgba(0,0,0,0.2);  /* light color */
  border-radius: 2px;
}

.scrollable-errors::-webkit-scrollbar-track {
  background-color: transparent;      /* keep track transparent */
}

/* For Firefox */
.scrollable-errors {
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.2) transparent;
}
</style>
